import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Eye, EyeOff, Gamepad2 } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const Games = () => {
  const [games, setGames] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');

  useEffect(() => {
    fetchGames();
    fetchCategories();
  }, [selectedCategory, selectedType, search]);

  const fetchGames = async () => {
    try {
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      if (selectedType) params.gameType = selectedType;
      if (search) params.search = search;

      const { data } = await api.get('/games', { params });
      setGames(data.data);
    } catch (error) {
      toast.error('Error fetching games');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/categories');
      setCategories(data.data);
    } catch (error) {
      console.error('Error fetching categories');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this game?')) return;

    try {
      await api.delete(`/games/${id}`);
      toast.success('Game deleted successfully');
      fetchGames();
    } catch (error) {
      toast.error('Error deleting game');
    }
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      await api.put(`/games/${id}`, { isActive: !currentStatus });
      toast.success(`Game ${!currentStatus ? 'activated' : 'deactivated'}`);
      fetchGames();
    } catch (error) {
      toast.error('Error updating game');
    }
  };

  if (loading) {
    return <div className="text-center py-20">Loading games...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold mb-2">Games Management</h1>
          <p className="text-gray-400">Manage all your games here</p>
        </div>
        <Link
          to="/games/new"
          className="flex items-center gap-2 px-6 py-3 gradient-primary text-white font-semibold rounded-lg hover:opacity-90 transition-opacity"
        >
          <Plus size={20} />
          Add New Game
        </Link>
      </div>

      {/* Filters */}
      <div className="gradient-card border border-gray-800 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search games..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-dark border border-gray-700 rounded-lg focus:outline-none focus:border-primary text-white"
            />
          </div>

          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-3 bg-dark border border-gray-700 rounded-lg focus:outline-none focus:border-primary text-white"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-4 py-3 bg-dark border border-gray-700 rounded-lg focus:outline-none focus:border-primary text-white"
          >
            <option value="">All Types</option>
            <option value="offline">Offline</option>
            <option value="online">Online</option>
          </select>

          <button
            onClick={() => {
              setSearch('');
              setSelectedCategory('');
              setSelectedType('');
            }}
            className="px-4 py-3 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Games Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {games.map((game) => (
          <div key={game.id} className="gradient-card border border-gray-800 rounded-xl overflow-hidden group hover:border-primary transition-colors">
            <div className="relative h-48 bg-gradient-to-br from-purple-600 to-cyan-600 overflow-hidden">
              {game.thumbnail ? (
                <img src={game.thumbnail} alt={game.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-6xl">
                  {game.icon}
                </div>
              )}
              <div className="absolute top-3 right-3 flex gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${game.isActive ? 'bg-green-500' : 'bg-red-500'} text-white`}>
                  {game.isActive ? 'Active' : 'Inactive'}
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-500 text-white">
                  {game.gameType}
                </span>
              </div>
            </div>

            <div className="p-4">
              <h3 className="font-bold text-lg mb-2 truncate">{game.name}</h3>
              <p className="text-sm text-gray-400 mb-3 line-clamp-2">{game.description}</p>

              <div className="flex items-center justify-between text-sm text-gray-400 mb-4">
                <span className="flex items-center gap-1">
                  <span className="text-primary">▶</span> {game.playCount || 0} plays
                </span>
                <span>⭐ {game.rating || 0}/5</span>
              </div>

              <div className="flex items-center gap-2">
                <Link
                  to={`/games/edit/${game.id}`}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary/20 text-primary hover:bg-primary/30 rounded-lg transition-colors"
                >
                  <Edit size={16} />
                  Edit
                </Link>

                <button
                  onClick={() => toggleActive(game.id, game.isActive)}
                  className="flex items-center justify-center px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                  title={game.isActive ? 'Deactivate' : 'Activate'}
                >
                  {game.isActive ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>

                <button
                  onClick={() => handleDelete(game.id)}
                  className="flex items-center justify-center px-3 py-2 bg-red-500/20 text-red-500 hover:bg-red-500/30 rounded-lg transition-colors"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {games.length === 0 && (
        <div className="text-center py-20">
          <Gamepad2 size={64} className="mx-auto text-gray-600 mb-4" />
          <p className="text-gray-400 text-lg">No games found</p>
          <Link to="/games/new" className="inline-block mt-4 text-primary hover:underline">
            Add your first game
          </Link>
        </div>
      )}
    </div>
  );
};

export default Games;